import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import './styles/shadcn-migration.css';
import './i18n';
import './dayjsSetup';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { ConfigProvider, ThemeConfig } from 'antd';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { THEME_VARIABLE } from './config/theme.config';
import { DIRECTION_LTR, DIRECTION_RTL, LOCAL_EN } from './config';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { defaultQueryOptions } from './config/queryOptions';
import useLanguageStore from './stores/useLanguageStore';
import * as Sentry from '@sentry/react';
import { initSentry } from './config/sentry.config';
import { ErrorFallback } from './components';
import { registerServiceWorker } from './pwa-config/notifications';
import { loadCustomerIO } from './customerIO-loader';
import { BrowserRouter as Router } from 'react-router-dom';
import { DirectionProvider } from '@radix-ui/react-direction';
import { bindQueryCacheToAccount } from './utils/bindQueryCacheToAccount';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...defaultQueryOptions,
    },
  },
});
bindQueryCacheToAccount(queryClient);

// Initialize Sentry with tracing
initSentry();

registerServiceWorker();

// A tab opened before a deploy asks for chunk hashes that no longer exist; reload once to get the new build.
window.addEventListener('vite:preloadError', (event) => {
  try {
    // At most one reload a minute, so a chunk that is really missing cannot loop.
    if (Date.now() - Number(sessionStorage.getItem('chunk-reload-at')) < 60_000) return;
    sessionStorage.setItem('chunk-reload-at', String(Date.now()));
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

export function Main() {
  const { i18n } = useTranslation();
  const { language } = useLanguageStore();

  const theme: ThemeConfig = {
    token: {
      ...THEME_VARIABLE,
      // Override Ant Design's built-in fontFamily token for Arabic so all
      // AntD components (which read font from this token) use Alexandria.
      ...(language !== LOCAL_EN && { fontFamily: 'Alexandria, sans-serif' }),
    },
  };

  useEffect(() => {
    // Set direction and language attribute on the document
    document.documentElement.dir = language === LOCAL_EN ? DIRECTION_LTR : DIRECTION_RTL;
    document.documentElement.lang = language;

    // Set the font family dynamically based on the selected language
    document.body.style.fontFamily =
      i18n.language === LOCAL_EN ? 'Lato, sans-serif' : 'Alexandria, sans-serif';
  }, [i18n.language]);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  useEffect(() => {
    loadCustomerIO();
  }, []);

  return (
    <ConfigProvider theme={theme} direction={language === LOCAL_EN ? DIRECTION_LTR : DIRECTION_RTL}>
      {/* Radix reads direction from here. Without it every popover, dropdown and
          select treats align="start" as "left", so in Arabic they anchor to the
          wrong edge of their trigger and get shoved off-screen by collision
          detection. AntD already gets its direction from ConfigProvider above. */}
      <DirectionProvider dir={language === LOCAL_EN ? DIRECTION_LTR : DIRECTION_RTL}>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen={false} />
            <Router>
              <Sentry.ErrorBoundary fallback={(props) => <ErrorFallback {...props} />}>
                <App />
              </Sentry.ErrorBoundary>
            </Router>
          </QueryClientProvider>
        </I18nextProvider>
      </DirectionProvider>
    </ConfigProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Main />
  </React.StrictMode>,
);
